import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { Avatar } from 'components/layout/Avatar';
import AttachmentViewer from 'components/forms/fieldsets/AttachmentViewer';
import EmailMessageViewer from '../email/EmailMessageViewer';
import { Task } from '../../views/tasks/TaskTypes';
import TicketTaskCard from './TicketTaskCard';
import { renderTicketLinkedItemBadge, renderTicketLinkedItemName, useOpenTicketLinkedItem } from './TicketLinkedItemsCard';
import { Ticket, TicketMessage } from './TicketTypes';

// An entry of the conversation, either a message or a task, sorted on its date
type ConversationEntry =
    | { kind: 'message'; date: string; message: TicketMessage }
    | { kind: 'task'; date: string; task: Task };

interface TicketConversationProps {
    ticket: Ticket;
    messages: TicketMessage[];
    tasks: Task[];
    onTaskChanged: () => void;
}

// The base styling of a message body inside its shadow root
const MESSAGE_BODY_STYLE = ':host{display:block;font-family:inherit;font-size:inherit;line-height:1.3;color:inherit;word-break:break-word;} img{max-width:100%;} p{margin:0;} blockquote{border-left:2px solid #ddd;margin:0.5rem 0;padding-left:0.75rem;color:#777;} span.mention{color:var(--general-blue);}';

// The dark and night themes drop the own text and background colors of an email so black text stays readable
const MESSAGE_BODY_DARK_STYLE = '*:not(a):not(.mention){color:inherit !important;} *{background-color:transparent !important;} a{color:var(--hyperlink-color) !important;} blockquote{border-left-color:#555 !important;}';

// Renders the html body of a message inside a shadow root so email styling cannot leak
const MessageBody: React.FC<{ html: string }> = ({ html }) => {
    const hostRef = useRef<HTMLDivElement>(null);
    const { resolvedTheme } = useSettings();

    // Write the html into the shadow root whenever it or the theme changes
    useEffect(() => {
        if (!hostRef.current) return;
        const shadowRoot = hostRef.current.shadowRoot || hostRef.current.attachShadow({ mode: 'open' });
        const themeStyle = resolvedTheme === 'light' ? '' : MESSAGE_BODY_DARK_STYLE;
        shadowRoot.innerHTML = `<style>${MESSAGE_BODY_STYLE}${themeStyle}</style>${html}`;
    }, [html, resolvedTheme]);

    return <div ref={hostRef} />;
};

// The avatar next to a message, the profile picture of the user or the initials of the name
const MessageAvatar: React.FC<{ name: string | null; picture?: string | null }> = ({ name, picture }) => {
    const [pictureFailed, setPictureFailed] = useState(false);

    // Show the initials when there is no picture or the picture cannot be loaded
    if (!picture || pictureFailed) {
        return <Avatar name={name} size='2rem' />;
    }
    return <img className='ticket-avatar-picture' src={picture} alt='' onError={() => setPictureFailed(true)} />;
};

// Formats how long ago a date is, in minutes, hours or days
const formatTimeAgo = (dateString: string, t: (key: string, options?: Record<string, any>) => string) => {
    const diffMinutes = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
    if (diffMinutes < 1) return t('date_time.relative_time.just_now');
    if (diffMinutes < 60) return t('date_time.relative_time.minutes_ago', { count: diffMinutes });
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return t('date_time.relative_time.hours_ago', { count: diffHours });
    return t('date_time.relative_time.days_ago', { count: Math.floor(diffHours / 24) });
};

// Returns the local day key of a date to group the entries on
const getDayKey = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

// The chat like conversation of a ticket, oldest entry on top and newest at the bottom
const TicketConversation: React.FC<TicketConversationProps> = ({ ticket, messages, tasks, onTaskChanged }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const openLinkedItem = useOpenTicketLinkedItem();
    const scrollRef = useRef<HTMLDivElement>(null);

    // Open the sent email of a message in the email viewer modal
    const openSentEmail = (message: TicketMessage) => {
        const email = message.email_message_data;
        if (!email) return;
        initializeModal(
            <EmailMessageViewer email={email} mode='modal' sentByName={message.author_name} sentDate={formatTimelineDate(message.created, false)} />,
            { modalSize: 'medium', title: email.subject || t('ticket.conversation.sent_email_title') }
        );
    };

    // Open an attachment of a message in the attachment viewer modal
    const openAttachment = (attachment: { file: string; filename: string }) => {
        initializeModal(
            <AttachmentViewer file={attachment.file} filename={attachment.filename} type='attachment' />,
            { modalSize: 'medium-large' }
        );
    };

    // The icon which opens the sent email of a message
    const renderEmailIcon = (message: TicketMessage) => (
        <span className='ticket-email-icon tooltip-icon' onClick={() => openSentEmail(message)}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <span className='tooltip tooltip-left'>{t('ticket.conversation.view_email_tooltip')}</span>
        </span>
    );

    // Merge the messages and tasks into one list sorted on date
    const entries: ConversationEntry[] = [
        ...messages.map(message => ({ kind: 'message' as const, date: message.created, message })),
        ...tasks.map(task => ({ kind: 'task' as const, date: task.created || '', task })),
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Scroll the conversation to the newest entry when it changes
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages.length, tasks.length]);

    // Scroll to the newest entry once the conversation becomes visible, a hidden modal has no height yet
    useEffect(() => {
        const element = scrollRef.current;
        if (!element || typeof ResizeObserver === 'undefined') return;
        let scrolled = false;
        const observer = new ResizeObserver(() => {
            if (scrolled || element.clientHeight === 0) return;
            element.scrollTop = element.scrollHeight;
            scrolled = true;
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [ticket.id]);

    // Determine the display name of the sender of a message for the avatar
    const getSenderName = (message: TicketMessage) => {
        if (message.direction === 'inbound') {
            return message.from_name || ticket.reporter_display_name || message.from_email;
        }
        return message.author_name || message.from_name;
    };

    // Determine the kind of a message entry, notes and messages of both sides form their own runs
    const getMessageKind = (message: TicketMessage) => message.type === 'note' ? 'note' : message.direction;

    // Show the time below the last message of a run of messages with the same kind and time, and below every sent email
    const showsMeta = (index: number) => {
        const entry = entries[index];
        const next = entries[index + 1];
        if (entry.kind !== 'message' || !next || next.kind !== 'message' || next.message.type === 'system' || next.message.type === 'link') return true;
        if (entry.message.email_message_data) return true;
        if (getMessageKind(next.message) !== getMessageKind(entry.message)) return true;
        if (getDayKey(next.date) !== getDayKey(entry.date)) return true;
        return formatTimeAgo(next.date, t) !== formatTimeAgo(entry.date, t);
    };

    // Build the meta line below a message with how long ago it was sent and the note label
    const renderMeta = (message: TicketMessage) => {
        const parts = [formatTimeAgo(message.created, t)];
        if (message.type === 'note') parts.push(t('ticket.conversation.note_label'));
        if (message.delivery_status === 'failed') parts.push(t('ticket.conversation.delivery_failed_label'));
        if (message.delivery_status === 'queued') parts.push(t('ticket.conversation.delivery_queued_label'));
        return (
            <div className={`ticket-message-meta ${message.delivery_status === 'failed' ? 'delivery-failed' : ''}`}>
                {parts.join(' · ')}
                {message.email_message_data && renderEmailIcon(message)}
            </div>
        );
    };

    // Render a centered date line when the day of an entry differs from the previous entry
    let previousDayKey: string | null = null;
    const renderDateLine = (date: string) => {
        const dayKey = getDayKey(date);
        if (dayKey === previousDayKey) return null;
        previousDayKey = dayKey;
        return (
            <div className='ticket-entry ticket-entry-date'>
                <span>{formatTimelineDate(date, true)}</span>
            </div>
        );
    };

    return (
        <div className='ticket-conversation' ref={scrollRef}>
            {entries.length === 0 && (
                <p className='ticket-conversation-empty'>{t('ticket.conversation.empty_message')}</p>
            )}
            {entries.map((entry, index) => {
                // Render a task card on the right side
                if (entry.kind === 'task') {
                    return (
                        <React.Fragment key={`task-${entry.task.id}`}>
                            {renderDateLine(entry.date)}
                            <div className='ticket-entry ticket-entry-right ticket-entry-task'>
                                <TicketTaskCard task={entry.task} ticketId={ticket.id} onChanged={onTaskChanged} />
                            </div>
                        </React.Fragment>
                    );
                }

                const message = entry.message;

                // Render the automatic acknowledgement as a centered system line which opens the sent email
                if (message.is_acknowledgement) {
                    return (
                        <React.Fragment key={`message-${message.id}`}>
                            {renderDateLine(entry.date)}
                            <div className='ticket-entry ticket-entry-system'>
                                <span>
                                    {t('ticket.conversation.acknowledgement_sent_label')}
                                    {message.email_message_data && renderEmailIcon(message)}
                                </span>
                            </div>
                        </React.Fragment>
                    );
                }

                // Render a created proposal, job or deal as a centered line with a link and its status badge
                if (message.type === 'link') {
                    const linkedItem = message.linked_item;
                    return (
                        <React.Fragment key={`message-${message.id}`}>
                            {renderDateLine(entry.date)}
                            <div className='ticket-entry ticket-entry-system ticket-entry-link'>
                                <span>
                                    {t(`ticket.conversation.${message.subject}_created_label`)}
                                    {linkedItem ? (
                                        <span className='ticket-link-item' onClick={() => openLinkedItem(linkedItem)}>
                                            <span className='ticket-link-name'>{renderTicketLinkedItemName(linkedItem)}</span>
                                            {renderTicketLinkedItemBadge(linkedItem)}
                                        </span>
                                    ) : (
                                        <span className='ticket-link-removed'>{t('ticket.conversation.linked_item_removed_label')}</span>
                                    )}
                                    {message.author_name ? ` · ${message.author_name}` : ''}
                                </span>
                            </div>
                        </React.Fragment>
                    );
                }

                // Render a status change as a centered system line
                if (message.type === 'system') {
                    return (
                        <React.Fragment key={`message-${message.id}`}>
                            {renderDateLine(entry.date)}
                            <div className='ticket-entry ticket-entry-system'>
                                <span>
                                    {t('ticket.conversation.status_changed_label', { status: t(`ticket.status.${message.body}`).toLowerCase() })}
                                    {message.author_name ? ` · ${message.author_name}` : ''}
                                </span>
                            </div>
                        </React.Fragment>
                    );
                }

                // Render reporter messages on the left and user messages and notes on the right
                const isInbound = message.direction === 'inbound';
                const sideClass = isInbound ? 'ticket-entry-left' : 'ticket-entry-right';
                const typeClass = message.type === 'note' ? 'ticket-bubble-note' : isInbound ? 'ticket-bubble-inbound' : 'ticket-bubble-outbound';

                return (
                    <React.Fragment key={`message-${message.id}`}>
                        {renderDateLine(entry.date)}
                        <div className={`ticket-entry ${sideClass}`}>
                            <div className='ticket-entry-avatar'>
                                <MessageAvatar name={getSenderName(message)} picture={isInbound ? null : message.author_profile_picture} />
                            </div>
                            <div className='ticket-entry-content'>
                            <div className={`ticket-bubble ${typeClass}`}>
                                <div className='ticket-bubble-body'>
                                    {message.body && message.body.includes('<')
                                        ? <MessageBody html={message.body} />
                                        : <p>{message.body_text || message.body}</p>
                                    }
                                </div>
                                {message.attachments && message.attachments.length > 0 && (
                                    <div className='ticket-bubble-attachments'>
                                        {message.attachments.map(attachment => (
                                            <span key={attachment.id} className='ticket-bubble-attachment' onClick={() => openAttachment(attachment)}>
                                                {attachment.filename}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {showsMeta(index) && renderMeta(message)}
                            </div>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default TicketConversation;
